import { Controller } from '@hotwired/stimulus';

import {
    run as cc_run,
    reset as cc_reset,
    getCookie as cc_getCookie,
} from 'vanilla-cookieconsent';

import '@fify/websites-bundle/styles/cookieconsent.scss';

import {
    trans,

    COOKIE_CONSENT_TITLE_CONSENT,
    COOKIE_CONSENT_TITLE_PREFERENCES,
    COOKIE_CONSENT_DESCRIPTION,
    COOKIE_CONSENT_ACCEPT_ALL,
    COOKIE_CONSENT_REJECT_ALL,
    COOKIE_CONSENT_MANAGE_PREFERENCES,
    COOKIE_CONSENT_SAVE_PREFERENCES,
    COOKIE_CONSENT_CLOSE,
    COOKIE_CONSENT_SERVICE_COUNTER,
    COOKIE_CONSENT_PRIVACY_POLICY,
    COOKIE_CONSENT_TERMS_AND_CONDITIONS,
    COOKIE_CONSENT_ALWAYS_ENABLED,
    COOKIE_CONSENT_SECTION_START_TITLE,
    COOKIE_CONSENT_SECTION_START_DESCRIPTION,
    COOKIE_CONSENT_SECTION_NECESSARY_TITLE,
    COOKIE_CONSENT_SECTION_NECESSARY_DESCRIPTION,
    COOKIE_CONSENT_SECTION_ANALYTICS_TITLE,
    COOKIE_CONSENT_SECTION_ANALYTICS_DESCRIPTION,
    COOKIE_CONSENT_SECTION_MORE_TITLE,
    COOKIE_CONSENT_SECTION_MORE_DESCRIPTION,
} from '../../../../../assets/translator';


export default class CookieConsentController extends Controller {
    static values = {
        tosLink:     String,
        privacyLink: String,

        reCaptcha: { type: Boolean, default: false },
        analytics: { type: Array, default: [] },
        revision:  { type: Number, default: 1 },
    };

    /** @type {CookieConsentConfig} */
    static _config;

    /**
     * @returns {String}
     */
    get privacyLink() {
        return this.privacyLinkValue;
    }

    /**
     * @returns {String}
     */
    get tosLink() {
        return this.tosLinkValue;
    }

    /**
     * @returns {Boolean}
     */
    get reCaptcha() {
        return this.reCaptchaValue;
    }

    /**
     * @returns {Array|null}
     */
    get analytics() {
        if (this.hasAnalyticsValue && this.analyticsValue.length) {
            return this.analyticsValue;
        }

        return null;
    }

    /**
     * @returns {Number}
     */
    get revision() {
        return this.revisionValue;
    }


    initialize() {
        window.dispatchEvent(new CustomEvent('cc:onInitialize', {
            detail: {
                cookie: cc_getCookie(),
            },
        }));
    }

    async connect() {
        const config    = this._buildConfig();
        config.root     = this.element;
        config.autoShow = this._shouldShow();

        await cc_run(config);
        window.cookie_consent_initial_state = cc_getCookie();
    }

    disconnect() {
        cc_reset();
    }

    /**
     * @returns {Boolean}
     * @private
     */
    _shouldShow() {
        return window.location.href !== (new URL(this.tosLink, window.location.href)).toString()
            && window.location.href !== (new URL(this.privacyLink, window.location.href)).toString();
    }

    /**
     * @returns {CookieConsentConfig}
     * @private
     */
    _buildConfig() {
        if (CookieConsentController._config) {
            return CookieConsentController._config;
        }

        return CookieConsentController._config = {
            revision: this.revision,

            disablePageInteraction: true,

            guiOptions: {
                consentModal: {
                    layout:             'box',
                    position:           'middle center',
                    equalWeightButtons: false,
                    flipButtons:        false,
                },

                preferencesModal: {
                    layout:             'bar wide',
                    position:           'left',
                    equalWeightButtons: false,
                    flipButtons:        false,
                },
            },

            categories: this._buildCategories(),

            language: {
                default:      'symfony',
                translations: {
                    symfony: this._buildTranslations(),
                },
            },
        };
    }

    /**
     * @return {Object<Category>}
     * @private
     */
    _buildCategories() {
        const categories = {
            necessary: {
                readOnly: true,
                services: {},
            },
        };

        if (this.reCaptcha) {
            categories.necessary.services[google_recaptcha] = {
                label: 'Google reCAPTCHA',
                    cookies: [
                    {
                        name: '_GRECAPTCHA'
                    }
                ]
            };
        }

        const analytics = this.analytics;

        if (analytics) {
            categories.analytics = {
                enabled:  true,
                services: {},
            };

            if (analytics.indexOf('google') > -1) {
                categories.analytics.services.google_analytics = {
                    label:   'Google Analytics',
                    cookies: [
                        {
                            name: /^(_ga|_gid|_gcl)/
                        }
                    ]
                }
            }

            if (analytics.indexOf('matomo') > -1) {
                categories.analytics.services.matomo = {
                    label: 'Matomo',
                    cookies: [
                        {
                            name: /^(_pk_id|_pk_ses)/
                        }
                    ]
                }
            }
        }

        return categories;
    }

    /**
     * @return {Translation}
     * @private
     */
    _buildTranslations() {
        const sections = [
            {
                title:       trans(COOKIE_CONSENT_SECTION_START_TITLE),
                description: trans(COOKIE_CONSENT_SECTION_START_DESCRIPTION),
            },
            {
                title:          trans(COOKIE_CONSENT_SECTION_NECESSARY_TITLE) + ` <span class="pm__badge">${trans(COOKIE_CONSENT_ALWAYS_ENABLED)}</span>`,
                description:    trans(COOKIE_CONSENT_SECTION_NECESSARY_DESCRIPTION),
                linkedCategory: 'necessary',
            },
        ];

        if (this.analytics) {
            sections.push({
                title:          trans(COOKIE_CONSENT_SECTION_ANALYTICS_TITLE),
                description:    trans(COOKIE_CONSENT_SECTION_ANALYTICS_DESCRIPTION),
                linkedCategory: 'analytics',
            });
        }

        sections.push({
            title:       trans(COOKIE_CONSENT_SECTION_MORE_TITLE),
            description: trans(COOKIE_CONSENT_SECTION_MORE_DESCRIPTION),
        });

        return {
            consentModal: {
                title:              trans(COOKIE_CONSENT_TITLE_CONSENT),
                description:        trans(COOKIE_CONSENT_DESCRIPTION),
                acceptAllBtn:       trans(COOKIE_CONSENT_ACCEPT_ALL),
                acceptNecessaryBtn: trans(COOKIE_CONSENT_REJECT_ALL),
                showPreferencesBtn: trans(COOKIE_CONSENT_MANAGE_PREFERENCES),
                footer:             `<a href="${this.privacyLink}">${trans(COOKIE_CONSENT_PRIVACY_POLICY)}</a>\n<a href="${this.tosLink}">${trans(COOKIE_CONSENT_TERMS_AND_CONDITIONS)}</a>`,
            },

            preferencesModal: {
                title:               trans(COOKIE_CONSENT_TITLE_PREFERENCES),
                acceptAllBtn:        trans(COOKIE_CONSENT_ACCEPT_ALL),
                acceptNecessaryBtn:  trans(COOKIE_CONSENT_REJECT_ALL),
                savePreferencesBtn:  trans(COOKIE_CONSENT_SAVE_PREFERENCES),
                closeIconLabel:      trans(COOKIE_CONSENT_CLOSE),
                serviceCounterLabel: trans(COOKIE_CONSENT_SERVICE_COUNTER),
                sections:            sections,
            },
        };
    }
}
